import React, {useState, useEffect} from 'react'
import styled from 'styled-components'
import {ContentTypes} from '../core/_constants'
import {useMoveToFolderData} from '../core/FileToFolderAuth'
import {
  getAllContent,
  getBreadcrumbList,
  getAllFoldersList,
  getCompanyChildrenList,
} from '../core/_requests'
import {toast} from 'react-toastify'
import {toAbsoluteUrl} from '../../../../_metronic/helpers/index'
import {useIntl} from 'react-intl'
import {useAuth} from '../../auth'
import folderIcon from '../../../../_metronic/assets/images/svg/data-room/blue-folder-icon.svg'
import {Spinner} from '../../widgets/components/General/Spinner'

const StyledTree = styled.div`
  line-height: 1.5;
`

const StyledFolder = styled.div`
  padding-left: 20px;

  .folder--label {
    display: flex;
    align-items: center;
    span {
      margin-left: 5px;
    }
  }
`
const Collapsible = styled.div<{isOpen: boolean}>`
  height: ${(p) => (p.isOpen ? 'auto' : '0')};
  overflow: hidden;
`

const Folder = ({
  name,
  children,
  content,
  getBreadcrumb,
  isOpenNew,
  toggleFolder,
  companyFolderContentid,
  getCompanyFolderChildren,
}: {
  name: string
  children: any
  content: string
  getBreadcrumb: (value: string) => void
  isOpenNew: any
  toggleFolder: any
  companyFolderContentid: string
  getCompanyFolderChildren: (value: string) => void
}) => {
  const {storecontentID, addLoaderMoveToFolder, setAddLoaderMoveToFolder} = useMoveToFolderData()
  const handleToggleResponse = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      let contentId: string = e.target.value.substring(6)
      storecontentID(contentId)
      contentId = ''
    }
  }

  const handleToggle = () => {
    toggleFolder()
    if (content === companyFolderContentid) {
      getCompanyFolderChildren(companyFolderContentid)
    } else {
      getBreadcrumb(content)
    }
    setAddLoaderMoveToFolder(true)
  }

  return (
    <StyledFolder>
      <div className='folder--label'>
        <input
          className='tree-radio-button'
          type='radio'
          id={`FOLDER${content}`}
          name='folder_list'
          value={`FOLDER${content}`}
          onChange={handleToggleResponse}
        />
        {isOpenNew ? (
          <img
            height={24}
            width={24}
            alt='caret-down'
            className='me-2'
            src={toAbsoluteUrl('/media/svg/files/caret-down.svg')}
            onClick={() => {
              handleToggle()
            }}
          />
        ) : (
          <img
            height={24}
            width={24}
            alt='caret-right'
            className='me-2'
            src={toAbsoluteUrl('/media/svg/files/caret-right.svg')}
            onClick={() => handleToggle()}
          />
        )}
        <img height={24} width={24} alt='folder' className='me-2' src={folderIcon} />
        <span>{name}</span>
      </div>

      <Collapsible isOpen={isOpenNew}>
        {addLoaderMoveToFolder ? (
          <div
            className='position-absolute d-flex justify-content-center align-items-center'
            style={{left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
          >
            <Spinner />{' '}
          </div>
        ) : null}
        {children}
      </Collapsible>
    </StyledFolder>
  )
}

const TreeRecursive = ({
  data,
  toBeMovedId,
  companyFolderContentid,
}: {
  data: any
  toBeMovedId?: string
  companyFolderContentid: string
}) => {
  const [childrenList, setChildrenList] = useState<any>()
  const [openFolderIndex, setOpenFolderIndex] = useState(null)
  const {formatMessage} = useIntl()
  const {setAddLoaderMoveToFolder} = useMoveToFolderData()
  const {companyId} = useAuth()
  const toggleFolder = (index: any) => {
    setOpenFolderIndex(index === openFolderIndex ? null : index)
  }

  const getBreadcrumb = async (parentId: any) => {
    try {
      const {
        data: {data, success: successData, errors: errorsData},
      } = await getBreadcrumbList(parentId)
      if (successData) {
        setChildrenList(data.results)
      } else {
        errorsData.forEach((error: string) => {
          toast.error(formatMessage({id: error}))
        })
      }
    } catch (err) {
      console.log(err)
    } finally {
      setAddLoaderMoveToFolder(false)
    }
  }

  const getCompanyFolderChildren = async (parentId: any) => {
    const compId: any = companyId
    try {
      const {
        data: {data, success: successData, errors: errorsData},
      } = await getCompanyChildrenList(parentId, compId)
      if (successData) {
        setChildrenList(data.results)
      } else {
        errorsData.forEach((error: string) => {
          toast.error(formatMessage({id: error}))
        })
      }
    } catch (err) {
      console.log(err)
    } finally {
      setAddLoaderMoveToFolder(false)
    }
  }

  if (!data) {
    return null
  }
  return data.map(
    (item: {type: string; name: any; childrens: any; contentId: string}, index: any) => {
      if (item.type === ContentTypes.FOLDER && item.contentId !== toBeMovedId) {
        return (
          <>
            <Folder
              name={item.name}
              content={item.contentId}
              getBreadcrumb={getBreadcrumb}
              isOpenNew={openFolderIndex === index}
              toggleFolder={() => toggleFolder(index)}
              companyFolderContentid={companyFolderContentid}
              getCompanyFolderChildren={getCompanyFolderChildren}
            >
              <TreeRecursive
                data={childrenList}
                toBeMovedId={toBeMovedId}
                companyFolderContentid={companyFolderContentid}
              />
            </Folder>
          </>
        )
      }
      return null
    }
  )
}
const Tree = ({
  data,
  children,
  toBeMovedId,
  companyFolderContentid,
}: {
  data: any
  children?: any
  toBeMovedId?: string
  companyFolderContentid: string
}) => {
  const isImparative = data && !children

  return (
    <StyledTree>
      {isImparative ? (
        <TreeRecursive
          data={data}
          toBeMovedId={toBeMovedId}
          companyFolderContentid={companyFolderContentid}
        />
      ) : (
        children
      )}
    </StyledTree>
  )
}

Tree.File = File
Tree.Folder = Folder

export default function Final({toBeMovedId}: {toBeMovedId?: string}) {
  interface DataRoomFile {
    id: number
    name: string
    contentId: string
    parentId: string
    companyId: number
    type: string
  }
  const [folderDataList, setFolderDataList] = useState<DataRoomFile[]>([])
  const [foldersCompany, setFoldersCompany] = useState<any>([])
  const [companyFolderContentid, setCompanyFolderContentid] = useState<string>('')
  const {formatMessage} = useIntl()
  const {companyId} = useAuth()
  useEffect(() => {
    const getAllFilesContent = async () => {
      try {
        const {
          data: {data, success, errors},
        } = await getAllContent()
        if (success) {
          setFolderDataList(data.results)
        } else {
          errors.forEach((error: string) => {
            toast.error(formatMessage({id: error}))
          })
        }
      } catch (err) {
        console.log(err)
      }
    }

    const getAllFolder = async () => {
      const cid: any = companyId
      try {
        const {
          data: {data, success: successData, errors: errorsData},
        } = await getAllFoldersList(cid)
        if (successData) {
          setCompanyFolderContentid(data.results[0]?.contentId)
          setFoldersCompany(data.results)
        } else {
          errorsData.forEach((error: string) => {
            toast.error(formatMessage({id: error}))
          })
        }
      } catch (err) {
        console.log(err)
      }
    }
    getAllFilesContent()
    getAllFolder()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  var combinedList = foldersCompany.concat(folderDataList)
  const foldersArray = folderDataList.filter((obj: {type: any}) => obj.type === ContentTypes.FOLDER)
  const folderCount = foldersArray.length
  if (folderCount !== 0) {
    return (
      <div className='Final'>
        <Tree
          data={combinedList}
          toBeMovedId={toBeMovedId}
          companyFolderContentid={companyFolderContentid}
        />
      </div>
    )
  } else {
    return <p>{formatMessage({id: 'Please wait while the folders are being loaded...'})}</p>
  }
}
